import type {
    ApiKey,
    CreateApiKeyData,
    CreateServiceConfigData,
    ServiceConfig,
    SettingsFilters,
    UpdateApiKeyData,
    UpdateServiceConfigData,
} from "@/types/settings";

// Mock data per API Keys
const mockApiKeys: ApiKey[] = [
    {
        id: "1",
        name: "OpenAI Production Key",
        service: "OpenAI",
        category: "AI/ML",
        type: "API Key",
        value: "sk-proj-***hidden***",
        description: "Chiave principale per GPT-4 e modelli OpenAI",
        isActive: true,
        expiresAt: new Date("2024-12-31"),
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-15"),
    },
    {
        id: "2",
        name: "Stripe Live Secret",
        service: "Stripe",
        category: "Payment",
        type: "Secret Key",
        value: "sk_live_***hidden***",
        description: "Chiave segreta per pagamenti in produzione",
        isActive: true,
        createdAt: new Date("2024-01-05"),
        updatedAt: new Date("2024-01-10"),
    },
    {
        id: "3",
        name: "SendGrid API Key",
        service: "SendGrid",
        category: "Email",
        type: "API Key",
        value: "SG.***hidden***",
        description: "API key per invio email transazionali",
        isActive: true,
        expiresAt: new Date("2024-06-30"),
        createdAt: new Date("2024-01-08"),
        updatedAt: new Date("2024-01-12"),
    },
    {
        id: "4",
        name: "AWS Access Key",
        service: "AWS",
        category: "Cloud",
        type: "API Key",
        value: "AKIA***hidden***",
        description: "Chiave di accesso per servizi AWS",
        isActive: true,
        createdAt: new Date("2024-01-03"),
        updatedAt: new Date("2024-01-18"),
    },
    {
        id: "5",
        name: "Google Analytics Token",
        service: "Google Analytics",
        category: "Analytics",
        type: "Token",
        value: "ya29.***hidden***",
        description: "Token OAuth2 per Google Analytics 4",
        isActive: false,
        expiresAt: new Date("2024-02-15"),
        createdAt: new Date("2024-01-02"),
        updatedAt: new Date("2024-01-20"),
    },
    {
        id: "6",
        name: "Twilio Auth Token",
        service: "Twilio",
        category: "Other",
        type: "Token",
        value: "***hidden***",
        description: "Token per servizi SMS e comunicazioni",
        isActive: true,
        createdAt: new Date("2024-01-07"),
        updatedAt: new Date("2024-01-14"),
    },
];

// Mock data per Service Configs
const mockServiceConfigs: ServiceConfig[] = [
    {
        id: "1",
        name: "OpenAI Configuration",
        category: "AI/ML",
        baseUrl: "https://api.openai.com/v1",
        isEnabled: true,
        config: {
            model: "gpt-4",
            maxTokens: 4000,
            temperature: 0.7,
        },
        createdAt: new Date("2024-01-01"),
        updatedAt: new Date("2024-01-15"),
    },
    {
        id: "2",
        name: "Stripe Payment Config",
        category: "Payment",
        baseUrl: "https://api.stripe.com/v1",
        isEnabled: true,
        config: {
            currency: "EUR",
            webhookEndpoint: "/webhooks/stripe",
            autoConfirm: true,
        },
        createdAt: new Date("2024-01-05"),
        updatedAt: new Date("2024-01-10"),
    },
    {
        id: "3",
        name: "SendGrid Email Config",
        category: "Email",
        baseUrl: "https://api.sendgrid.com/v3",
        isEnabled: true,
        config: {
            fromEmail: "noreply@company.com",
            fromName: "Company Name",
            templateId: "d-1234567890",
        },
        createdAt: new Date("2024-01-08"),
        updatedAt: new Date("2024-01-12"),
    },
    {
        id: "4",
        name: "AWS S3 Config",
        category: "Cloud",
        baseUrl: "https://s3.amazonaws.com",
        isEnabled: true,
        config: {
            bucket: "company-assets",
            region: "eu-west-1",
            cdnEnabled: true,
        },
        createdAt: new Date("2024-01-03"),
        updatedAt: new Date("2024-01-18"),
    },
];

// In-memory storage
let apiKeys = [...mockApiKeys];
let serviceConfigs = [...mockServiceConfigs];

// Helper functions per API Keys
export const apiKeyService = {
    // Get all API keys
    getAll: (): ApiKey[] => {
        return [...apiKeys];
    },

    // Get API key by ID
    getById: (id: string): ApiKey | undefined => {
        return apiKeys.find((key) => key.id === id);
    },

    // Create new API key
    create: (data: CreateApiKeyData): ApiKey => {
        const newKey: ApiKey = {
            id: (apiKeys.length + 1).toString(),
            ...data,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        apiKeys.push(newKey);
        return newKey;
    },

    // Update API key
    update: (id: string, data: UpdateApiKeyData): ApiKey | null => {
        const index = apiKeys.findIndex((key) => key.id === id);
        if (index === -1) return null;

        apiKeys[index] = {
            ...apiKeys[index],
            ...data,
            updatedAt: new Date(),
        };
        return apiKeys[index];
    },

    // Delete API key
    delete: (id: string): boolean => {
        const index = apiKeys.findIndex((key) => key.id === id);
        if (index === -1) return false;

        apiKeys.splice(index, 1);
        return true;
    },

    // Search and filter API keys
    search: (filters: SettingsFilters): ApiKey[] => {
        let filtered = [...apiKeys];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter(
                (key) =>
                    key.name.toLowerCase().includes(searchTerm) ||
                    key.service.toLowerCase().includes(searchTerm) ||
                    key.description?.toLowerCase().includes(searchTerm),
            );
        }

        if (filters.category) {
            filtered = filtered.filter((key) => key.category === filters.category);
        }

        if (filters.isActive !== undefined) {
            filtered = filtered.filter((key) => key.isActive === filters.isActive);
        }

        return filtered;
    },
};

// Helper functions per Service Configs
export const serviceConfigService = {
    // Get all service configs
    getAll: (): ServiceConfig[] => {
        return [...serviceConfigs];
    },

    // Get service config by ID
    getById: (id: string): ServiceConfig | undefined => {
        return serviceConfigs.find((config) => config.id === id);
    },

    // Create new service config
    create: (data: CreateServiceConfigData): ServiceConfig => {
        const newConfig: ServiceConfig = {
            id: (serviceConfigs.length + 1).toString(),
            ...data,
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        serviceConfigs.push(newConfig);
        return newConfig;
    },

    // Update service config
    update: (id: string, data: UpdateServiceConfigData): ServiceConfig | null => {
        const index = serviceConfigs.findIndex((config) => config.id === id);
        if (index === -1) return null;

        serviceConfigs[index] = {
            ...serviceConfigs[index],
            ...data,
            updatedAt: new Date(),
        };
        return serviceConfigs[index];
    },

    // Delete service config
    delete: (id: string): boolean => {
        const index = serviceConfigs.findIndex((config) => config.id === id);
        if (index === -1) return false;

        serviceConfigs.splice(index, 1);
        return true;
    },

    // Search and filter service configs
    search: (filters: SettingsFilters): ServiceConfig[] => {
        let filtered = [...serviceConfigs];

        if (filters.search) {
            const searchTerm = filters.search.toLowerCase();
            filtered = filtered.filter((config) => config.name.toLowerCase().includes(searchTerm) || config.baseUrl?.toLowerCase().includes(searchTerm));
        }

        if (filters.category) {
            filtered = filtered.filter((config) => config.category === filters.category);
        }

        if (filters.isActive !== undefined) {
            filtered = filtered.filter((config) => config.isEnabled === filters.isActive);
        }

        return filtered;
    },
};
